/**
 * Dados da vitrine: catálogo estático + /topics (público) + /auth/my-courses.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PLATFORM_COURSES } from '../../config/courses';
import { getAllTopics } from '../../services/topicService';
import { getMyCourses } from '../../services/authService';
import { mergeCourses, type ResolvedCourse } from './mergeCourses';

interface UsePlatformCoursesArgs {
  isAuthenticated: boolean;
  currentTopicId?: number;
}

interface UsePlatformCoursesResult {
  courses: ResolvedCourse[];
  /** Curso do hero. Cai no primeiro da lista se ninguém for `featured`. */
  featured?: ResolvedCourse;
  isLoading: boolean;
}

export function usePlatformCourses({
  isAuthenticated,
  currentTopicId,
}: UsePlatformCoursesArgs): UsePlatformCoursesResult {
  // Público e praticamente estático: vale um staleTime longo para não bater no
  // banco a cada visita anônima.
  const topicsQuery = useQuery({
    queryKey: ['topics'],
    queryFn: getAllTopics,
    staleTime: 1000 * 60 * 30,
    gcTime: 1000 * 60 * 60,
    retry: 1,
  });

  const myCoursesQuery = useQuery({
    queryKey: ['auth', 'my-courses'],
    queryFn: getMyCourses,
    enabled: isAuthenticated,
  });

  // A vitrine só anuncia curso que existe: entradas sem conteúdo no banco são
  // descartadas em vez de virarem card "em breve". Seguro porque `mergeCourses`
  // é otimista enquanto /topics carrega ou falha — nenhum curso real some da
  // home por um blip de rede.
  const courses = useMemo(
    () =>
      mergeCourses({
        catalog: PLATFORM_COURSES,
        topics: topicsQuery.data,
        topicsErrored: topicsQuery.isError,
        myCourses: isAuthenticated ? myCoursesQuery.data : undefined,
        currentTopicId,
      }).filter((course) => course.status === 'available'),
    [
      topicsQuery.data,
      topicsQuery.isError,
      myCoursesQuery.data,
      isAuthenticated,
      currentTopicId,
    ],
  );

  const featured = useMemo(
    () => courses.find((c) => c.featured) ?? courses[0],
    [courses],
  );

  return {
    courses,
    featured,
    // A vitrine nunca fica em branco esperando a rede: o catálogo já basta para
    // pintar. `isLoading` serve só para detalhes secundários (ex.: cadeados).
    isLoading: topicsQuery.isLoading || (isAuthenticated && myCoursesQuery.isLoading),
  };
}
